import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import {
  playerProfileService,
  clubCareerService,
  performanceStatsService,
  transferHistoryService,
  nationalTeamService,
  advancedPerformanceService,
  medicalRecordsService,
  competitionsPlayedService,
  marketValueHistoryService,
  playerMediaService } from
'../../../services/playerMasterService';
import { adminClubInterestService } from '../../../services/adminContentService';
import { useAuth } from '../../../contexts/AuthContext';
import { playerMediaStorage } from '../../../services/storageService';
import { playerChangeNotificationService } from '../../../services/playerChangeNotificationService';
import { supabase } from '../../../lib/supabase';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const AdminPlayerProfileModal = ({ player, onClose, isOpen, onPlayerUpdated }) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [activeTab, setActiveTab] = useState('overview');
  const [isFullEditMode, setIsFullEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingToggles, setSavingToggles] = useState({});

  // Player data states
  const [playerData, setPlayerData] = useState(player);
  const [editedPlayerData, setEditedPlayerData] = useState({});
  const [careerHistory, setCareerHistory] = useState([]);
  const [performanceStats, setPerformanceStats] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [nationalTeam, setNationalTeam] = useState(null);
  const [advancedPerformance, setAdvancedPerformance] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [marketValueHistory, setMarketValueHistory] = useState([]);
  const [media, setMedia] = useState([]);
  const [clubInterests, setClubInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (player?.id && isOpen) {
      console.log('AdminPlayerProfileModal: Setting player data', { playerId: player?.id, playerName: player?.fullName });
      setPlayerData(player);
      setEditedPlayerData(player);
      fetchAllPlayerData();
    } else if (isOpen && !player?.id) {
      console.error('AdminPlayerProfileModal: Modal opened without valid player ID', { player });
      alert('Error: Player ID is missing. Please close and reopen the modal.');
    }
  }, [player?.id, isOpen]);

  const fetchAllPlayerData = async () => {
    if (!player?.id) {
      console.error('Cannot fetch player data: missing player ID');
      return;
    }

    setLoading(true);
    try {
      const [careerRes, statsRes, transferRes, nationalRes, advancedRes, medicalRes, competitionsRes, marketValueRes, mediaRes, clubInterestRes] = await Promise.all([
      clubCareerService?.getByPlayerId(player?.id),
      performanceStatsService?.getByPlayerId(player?.id),
      transferHistoryService?.getByPlayerId(player?.id),
      nationalTeamService?.getSeniorByPlayerId(player?.id),
      advancedPerformanceService?.getByPlayerId(player?.id),
      medicalRecordsService?.getByPlayerId(player?.id),
      competitionsPlayedService?.getByPlayerId(player?.id),
      marketValueHistoryService?.getByPlayerId(player?.id),
      playerMediaService?.getByPlayerId(player?.id),
      adminClubInterestService?.getAll()]
      );
      setCareerHistory(careerRes?.data || []);
      setPerformanceStats(statsRes?.data || []);
      setTransferHistory(transferRes?.data || []);
      setNationalTeam(nationalRes?.data);
      setAdvancedPerformance(advancedRes?.data || []);
      setMedicalRecords(medicalRes?.data || []);
      setCompetitions(competitionsRes?.data || []);
      setMarketValueHistory(marketValueRes?.data || []);
      setMedia(mediaRes?.data || []);
      const playerInterests = clubInterestRes?.data?.filter((interest) => interest?.playerId === player?.id) || [];
      setClubInterests(playerInterests);
    } catch (error) {
      console.error('Error fetching player data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'fullName':
        if (!value || value?.trim()?.length < 2) {
          errors.fullName = 'Full name must be at least 2 characters';
        } else if (value?.length > 100) {
          errors.fullName = 'Full name must be less than 100 characters';
        } else if (!/^[a-zA-Z\s'-]+$/?.test(value)) {
          errors.fullName = 'Full name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;

      case 'dateOfBirth':
        if (value) {
          const date = new Date(value);
          const today = new Date();
          let age = today?.getFullYear() - date?.getFullYear();
          if (age < 13) {
            errors.dateOfBirth = 'Player must be at least 13 years old';
          } else if (age > 50) {
            errors.dateOfBirth = 'Please enter a valid date of birth';
          }
        }
        break;

      case 'height':
        if (value) {
          const num = parseFloat(value);
          if (isNaN(num) || num < 140 || num > 230) {
            errors.height = 'Height must be between 140-230 cm';
          }
        }
        break;

      case 'weight':
        if (value) {
          const num = parseFloat(value);
          if (isNaN(num) || num < 40 || num > 150) {
            errors.weight = 'Weight must be between 40-150 kg';
          }
        }
        break;

      case 'email':case 'agentEmail':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) {
          errors[field] = 'Please enter a valid email address';
        }
        break;

      case 'phone':case 'agentPhone':case 'emergencyContactPhone':
        if (value && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/?.test(value)) {
          errors[field] = 'Please enter a valid phone number';
        }
        break;

      case 'currentMarketValue':case 'highestMarketValue':
        if (value) {
          const num = parseFloat(value);
          if (isNaN(num) || num < 0) {
            errors[field] = 'Market value must be a positive number';
          }
        }
        break;

      case 'squadNumber':
        if (value) {
          const num = parseInt(value);
          if (isNaN(num) || num < 1 || num > 99) {
            errors[field] = 'Squad number must be between 1-99';
          }
        }
        break;
    }

    return errors;
  };

  const handleFieldChange = (field, value) => {
    setEditedPlayerData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Real-time validation
    const fieldErrors = validateField(field, value);
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (Object.keys(fieldErrors)?.length > 0) {
        Object.assign(newErrors, fieldErrors);
      } else {
        delete newErrors?.[field];
      }
      return newErrors;
    });
  };

  const handlePhotoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const toggleFullEditMode = () => {
    if (isFullEditMode) {
      // Exiting edit mode - reset changes
      setEditedPlayerData(playerData);
      setProfilePhoto(null);
      setPhotoPreview(null);
    }
    setIsFullEditMode(!isFullEditMode);
  };

  const handleSaveClick = () => {
    // Validate all fields
    if (!validateAllFields()) {
      alert('Please fix validation errors before saving');
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const validateAllFields = () => {
    const errors = {};

    // Required fields
    if (!editedPlayerData?.fullName || editedPlayerData?.fullName?.trim()?.length < 2) {
      errors.fullName = 'Full name is required (minimum 2 characters)';
    }

    // Validate all fields
    Object.keys(editedPlayerData)?.forEach((field) => {
      const fieldErrors = validateField(field, editedPlayerData?.[field]);
      Object.assign(errors, fieldErrors);
    });

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const saveAllChanges = async () => {
    if (!playerData?.id) {
      console.error('Cannot save: missing player ID', { playerData, editedPlayerData });
      alert('Error: Player ID is missing. Please close and reopen the modal.');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        fullName: editedPlayerData?.fullName,
        knownAs: editedPlayerData?.knownAs,
        dateOfBirth: editedPlayerData?.dateOfBirth || null,
        placeOfBirth: editedPlayerData?.placeOfBirth,
        nationalityPrimary: editedPlayerData?.nationalityPrimary,
        nationalitySecondary: editedPlayerData?.nationalitySecondary,
        height: editedPlayerData?.height ? parseFloat(editedPlayerData?.height) : null,
        weight: editedPlayerData?.weight ? parseFloat(editedPlayerData?.weight) : null,
        preferredFoot: editedPlayerData?.preferredFoot,
        primaryPosition: editedPlayerData?.primaryPosition,
        secondaryPositions: editedPlayerData?.secondaryPositions,
        email: editedPlayerData?.email,
        phone: editedPlayerData?.phone,
        address: editedPlayerData?.address,
        city: editedPlayerData?.city,
        country: editedPlayerData?.country,
        postalCode: editedPlayerData?.postalCode,
        currentClub: editedPlayerData?.currentClub,
        currentLeague: editedPlayerData?.currentLeague,
        currentCountry: editedPlayerData?.currentCountry,
        squadNumber: editedPlayerData?.squadNumber ? parseInt(editedPlayerData?.squadNumber) : null,
        contractType: editedPlayerData?.contractType,
        contractStartDate: editedPlayerData?.contractStartDate || null,
        contractEndDate: editedPlayerData?.contractEndDate || null,
        availableForTransfer: editedPlayerData?.availableForTransfer === 'Yes' || editedPlayerData?.availableForTransfer === true,
        availableForLoan: editedPlayerData?.availableForLoan === 'Yes' || editedPlayerData?.availableForLoan === true,
        currentMarketValue: editedPlayerData?.currentMarketValue ? parseFloat(editedPlayerData?.currentMarketValue) : null,
        highestMarketValue: editedPlayerData?.highestMarketValue ? parseFloat(editedPlayerData?.highestMarketValue) : null,
        agentName: editedPlayerData?.agentName,
        agencyName: editedPlayerData?.agencyName,
        agentEmail: editedPlayerData?.agentEmail,
        agentPhone: editedPlayerData?.agentPhone,
        representationContractUntil: editedPlayerData?.representationContractUntil || null,
        emergencyContactName: editedPlayerData?.emergencyContactName,
        emergencyContactRelation: editedPlayerData?.emergencyContactRelation,
        emergencyContactPhone: editedPlayerData?.emergencyContactPhone,
        previousClubs: editedPlayerData?.previousClubs,
        achievements: editedPlayerData?.achievements,
        medicalConditions: editedPlayerData?.medicalConditions,
        additionalNotes: editedPlayerData?.additionalNotes,
        photoFile: profilePhoto,
        oldPhotoPath: playerData?.profilePhoto
      };

      // Track changed fields for notification
      const fieldLabels = {
        fullName: 'Full Name',
        knownAs: 'Known As',
        dateOfBirth: 'Date of Birth',
        placeOfBirth: 'Place of Birth',
        nationalityPrimary: 'Primary Nationality',
        nationalitySecondary: 'Secondary Nationality',
        height: 'Height',
        weight: 'Weight',
        preferredFoot: 'Preferred Foot',
        primaryPosition: 'Primary Position',
        currentClub: 'Current Club',
        currentLeague: 'Current League',
        squadNumber: 'Squad Number',
        contractType: 'Contract Type',
        availableForTransfer: 'Available for Transfer',
        availableForLoan: 'Available for Loan',
        currentMarketValue: 'Current Market Value',
        agentName: 'Agent Name',
        agencyName: 'Agency Name'
      };

      const changedFields = playerChangeNotificationService?.formatChangedFields(
        playerData,
        editedPlayerData,
        fieldLabels
      );

      const result = await playerProfileService?.update(playerData?.id, updateData);

      if (result?.error) {
        console.error('Update player error:', result?.error);
        alert(`Failed to save changes: ${result?.error?.message || 'Unknown error'}`);
        return;
      }

      // Update local state with saved data
      const updatedPlayer = result?.data;

      // Load new profile photo URL if photo was uploaded
      if (profilePhoto && updatedPlayer?.profilePhoto) {
        const photoResult = await playerMediaStorage?.getSignedUrl(updatedPlayer?.profilePhoto, 3600);
        if (photoResult?.data?.signedUrl) {
          updatedPlayer.profilePhotoUrl = photoResult?.data?.signedUrl;
        }
      }

      setPlayerData(updatedPlayer);
      setEditedPlayerData(updatedPlayer);
      setProfilePhoto(null);
      setPhotoPreview(null);
      setIsFullEditMode(false);
      setShowConfirmDialog(false);

      // Send change notification
      if (changedFields?.length > 0) {
        await playerChangeNotificationService?.sendChangeNotification(
          updatedPlayer,
          changedFields,
          'Single Edit'
        );
      }

      // Notify parent component to refresh player list
      onPlayerUpdated?.();

      alert('All changes saved successfully!');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert(`Failed to save changes: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSave = async (field, value) => {
    const playerId = playerData?.id;
    if (!playerId) return;
    setSavingToggles((prev) => ({ ...prev, [field]: true }));
    try {
      const dbField = field === 'availableForTransfer' ? 'available_for_transfer' :
      field === 'availableForLoan' ? 'available_for_loan' :
      field === 'fifaVerified' ? 'fifa_verified' :
      'caf_verified';
      const { error } = await supabase?.from('player_master_profiles')?.update({ [dbField]: value, updated_at: new Date()?.toISOString() })?.eq('id', playerId);
      if (error) throw error;
      setPlayerData((prev) => ({ ...prev, [field]: value }));
      setEditedPlayerData((prev) => ({ ...prev, [field]: value }));
      if (onPlayerUpdated) onPlayerUpdated({ ...playerData, [field]: value });
    } catch (error) {
      console.error('Toggle save error:', error);
      alert(`Failed to update: ${error?.message}`);
    } finally {
      setSavingToggles((prev) => ({ ...prev, [field]: false }));
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today?.getDate() < birthDate?.getDate()) {
      age--;
    }
    return age;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatPosition = (position) => {
    if (!position) return 'N/A';
    return position?.split('-')?.map((word) =>
    word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const positions = [
  'Goalkeeper',
  'Right Back',
  'Left Back',
  'Center Back',
  'Defensive Midfielder',
  'Central Midfielder',
  'Attacking Midfielder',
  'Right Winger',
  'Left Winger',
  'Striker',
  'Forward'];


  const contractTypes = [
  'Permanent',
  'Loan',
  'Free Agent',
  'Youth Contract',
  'Professional Contract'];


  if (!player) return null;

  const tabs = [
  { id: 'overview', label: 'Overview', icon: 'User' },
  { id: 'career', label: 'Career History', icon: 'TrendingUp', count: careerHistory?.length },
  { id: 'statistics', label: 'Performance Stats', icon: 'BarChart3', count: performanceStats?.length },
  { id: 'advanced', label: 'Advanced Metrics', icon: 'Activity', count: advancedPerformance?.length },
  { id: 'competitions', label: 'Competitions', icon: 'Trophy', count: competitions?.length },
  { id: 'transfers', label: 'Transfer History', icon: 'ArrowRightLeft', count: transferHistory?.length },
  { id: 'national', label: 'National Team', icon: 'Flag' },
  { id: 'market', label: 'Market Value', icon: 'TrendingUp', count: marketValueHistory?.length },
  { id: 'medical', label: 'Medical Records', icon: 'Heart', count: medicalRecords?.length },
  { id: 'media', label: 'Media', icon: 'FileVideo', count: media?.length },
  { id: 'club-interest', label: 'Club Interest', icon: 'Users', count: clubInterests?.length },
  { id: 'agent', label: 'Agent Info', icon: 'Briefcase' }];


  const renderFullEditForm = () =>
  <div className="space-y-6">
      {/* Profile Photo Section */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Profile Photo</h4>
        <div className="flex items-center gap-4">
          {(photoPreview || playerData?.profilePhotoUrl) &&
        <img
          src={photoPreview || playerData?.profilePhotoUrl}
          alt="Profile preview"
          className="w-24 h-24 rounded-full object-cover border-2 border-primary" />

        }
          <div className="flex-1">
            <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" />
          
            <p className="text-xs text-muted-foreground mt-1">
              Upload a professional headshot (JPG, PNG, max 5MB)
            </p>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Personal Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
          label="Full Name *"
          value={editedPlayerData?.fullName || ''}
          onChange={(e) => handleFieldChange('fullName', e?.target?.value)}
          placeholder="Enter full name" />
        
          <Input
          label="Known As (Nickname)"
          value={editedPlayerData?.knownAs || ''}
          onChange={(e) => handleFieldChange('knownAs', e?.target?.value)}
          placeholder="Enter nickname" />
        
          <Input
          label="Date of Birth"
          type="date"
          value={editedPlayerData?.dateOfBirth || ''}
          onChange={(e) => handleFieldChange('dateOfBirth', e?.target?.value)} />
        
          <Input
          label="Place of Birth"
          value={editedPlayerData?.placeOfBirth || ''}
          onChange={(e) => handleFieldChange('placeOfBirth', e?.target?.value)}
          placeholder="City, Country" />
        
          <Input
          label="Primary Nationality"
          value={editedPlayerData?.nationalityPrimary || ''}
          onChange={(e) => handleFieldChange('nationalityPrimary', e?.target?.value)}
          placeholder="Enter nationality" />
        
          <Input
          label="Secondary Nationality"
          value={editedPlayerData?.nationalitySecondary || ''}
          onChange={(e) => handleFieldChange('nationalitySecondary', e?.target?.value)}
          placeholder="Enter secondary nationality" />
        
          <Input
          label="Height (cm)"
          type="number"
          value={editedPlayerData?.height || ''}
          onChange={(e) => handleFieldChange('height', e?.target?.value)}
          placeholder="e.g., 180" />
        
          <Input
          label="Weight (kg)"
          type="number"
          value={editedPlayerData?.weight || ''}
          onChange={(e) => handleFieldChange('weight', e?.target?.value)}
          placeholder="e.g., 75" />
        
          <Select
          label="Preferred Foot"
          value={editedPlayerData?.preferredFoot || ''}
          onChange={(e) => handleFieldChange('preferredFoot', e?.target?.value)}>
          
            <option value="">Select preferred foot</option>
            <option value="Right">Right</option>
            <option value="Left">Left</option>
            <option value="Both">Both</option>
          </Select>
          <Select
          label="Primary Position"
          value={editedPlayerData?.primaryPosition || ''}
          onChange={(e) => handleFieldChange('primaryPosition', e?.target?.value)}>
          
            <option value="">Select primary position</option>
            {positions?.map((pos) =>
          <option key={pos} value={pos}>{pos}</option>
          )}
          </Select>
          <Input
          label="Secondary Positions"
          value={editedPlayerData?.secondaryPositions || ''}
          onChange={(e) => handleFieldChange('secondaryPositions', e?.target?.value)}
          placeholder="e.g., CAM, RW"
          className="md:col-span-2" />
        
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Contact Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
          label="Email Address"
          type="email"
          value={editedPlayerData?.email || ''}
          onChange={(e) => handleFieldChange('email', e?.target?.value)}
          placeholder="player@example.com" />
        
          <Input
          label="Phone Number"
          value={editedPlayerData?.phone || ''}
          onChange={(e) => handleFieldChange('phone', e?.target?.value)}
          placeholder="+1234567890" />
        
          <Input
          label="Street Address"
          value={editedPlayerData?.address || ''}
          onChange={(e) => handleFieldChange('address', e?.target?.value)}
          placeholder="Enter street address"
          className="md:col-span-2" />
        
          <Input
          label="City"
          value={editedPlayerData?.city || ''}
          onChange={(e) => handleFieldChange('city', e?.target?.value)}
          placeholder="Enter city" />
        
          <Input
          label="Country"
          value={editedPlayerData?.country || ''}
          onChange={(e) => handleFieldChange('country', e?.target?.value)}
          placeholder="Enter country" />
        
          <Input
          label="Postal Code"
          value={editedPlayerData?.postalCode || ''}
          onChange={(e) => handleFieldChange('postalCode', e?.target?.value)}
          placeholder="Enter postal code" />
        
        </div>
      </div>

      {/* Professional Details */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Professional Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
          label="Current Club"
          value={editedPlayerData?.currentClub || ''}
          onChange={(e) => handleFieldChange('currentClub', e?.target?.value)}
          placeholder="Enter current club" />
        
          <Input
          label="Current League"
          value={editedPlayerData?.currentLeague || ''}
          onChange={(e) => handleFieldChange('currentLeague', e?.target?.value)}
          placeholder="Enter league name" />
        
          <Input
          label="Current Country"
          value={editedPlayerData?.currentCountry || ''}
          onChange={(e) => handleFieldChange('currentCountry', e?.target?.value)}
          placeholder="Enter country" />
        
          <Input
          label="Squad Number"
          type="number"
          value={editedPlayerData?.squadNumber || ''}
          onChange={(e) => handleFieldChange('squadNumber', e?.target?.value)}
          placeholder="e.g., 10" />
        
          <Select
          label="Contract Type"
          value={editedPlayerData?.contractType || ''}
          onChange={(e) => handleFieldChange('contractType', e?.target?.value)}>
          
            <option value="">Select contract type</option>
            {contractTypes?.map((type) =>
          <option key={type} value={type}>{type}</option>
          )}
          </Select>
          <Input
          label="Contract Start Date"
          type="date"
          value={editedPlayerData?.contractStartDate || ''}
          onChange={(e) => handleFieldChange('contractStartDate', e?.target?.value)} />
        
          <Input
          label="Contract End Date"
          type="date"
          value={editedPlayerData?.contractEndDate || ''}
          onChange={(e) => handleFieldChange('contractEndDate', e?.target?.value)} />
        
        </div>
      </div>

      {/* Transfer Status */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Transfer Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
          label="Available for Transfer"
          value={editedPlayerData?.availableForTransfer ? 'Yes' : 'No'}
          onChange={(e) => handleFieldChange('availableForTransfer', e?.target?.value)}>
          
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </Select>
          <Select
          label="Available for Loan"
          value={editedPlayerData?.availableForLoan ? 'Yes' : 'No'}
          onChange={(e) => handleFieldChange('availableForLoan', e?.target?.value)}>
          
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </Select>
          <Input
          label="Current Market Value (€M)"
          type="number"
          value={editedPlayerData?.currentMarketValue || ''}
          onChange={(e) => handleFieldChange('currentMarketValue', e?.target?.value)}
          placeholder="e.g., 5.0" />
        
          <Input
          label="Highest Market Value (€M)"
          type="number"
          value={editedPlayerData?.highestMarketValue || ''}
          onChange={(e) => handleFieldChange('highestMarketValue', e?.target?.value)}
          placeholder="e.g., 10.0" />
        
        </div>
      </div>

      {/* Agent & Representation */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Agent & Representation</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
          label="Agent Name"
          value={editedPlayerData?.agentName || ''}
          onChange={(e) => handleFieldChange('agentName', e?.target?.value)}
          placeholder="Enter agent name" />
        
          <Input
          label="Agency Name"
          value={editedPlayerData?.agencyName || ''}
          onChange={(e) => handleFieldChange('agencyName', e?.target?.value)}
          placeholder="Enter agency name" />
        
          <Input
          label="Agent Email"
          type="email"
          value={editedPlayerData?.agentEmail || ''}
          onChange={(e) => handleFieldChange('agentEmail', e?.target?.value)}
          placeholder="agent@example.com" />
        
          <Input
          label="Agent Phone"
          value={editedPlayerData?.agentPhone || ''}
          onChange={(e) => handleFieldChange('agentPhone', e?.target?.value)}
          placeholder="+1234567890" />
        
          <Input
          label="Representation Contract Until"
          type="date"
          value={editedPlayerData?.representationContractUntil || ''}
          onChange={(e) => handleFieldChange('representationContractUntil', e?.target?.value)} />
        
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
          label="Emergency Contact Name"
          value={editedPlayerData?.emergencyContactName || ''}
          onChange={(e) => handleFieldChange('emergencyContactName', e?.target?.value)}
          placeholder="Enter contact name" />
        
          <Input
          label="Relationship"
          value={editedPlayerData?.emergencyContactRelation || ''}
          onChange={(e) => handleFieldChange('emergencyContactRelation', e?.target?.value)}
          placeholder="e.g., Parent, Spouse" />
        
          <Input
          label="Emergency Phone Number"
          value={editedPlayerData?.emergencyContactPhone || ''}
          onChange={(e) => handleFieldChange('emergencyContactPhone', e?.target?.value)}
          placeholder="+1234567890" />
        
        </div>
      </div>

      {/* Career History */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Career History</h4>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Previous Clubs (List all clubs with years)
          </label>
          <textarea
          value={editedPlayerData?.previousClubs || ''}
          onChange={(e) => handleFieldChange('previousClubs', e?.target?.value)}
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
          placeholder="e.g., FC Barcelona Youth (2018-2020), Real Madrid B (2020-2022)" />
        
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Achievements & Honors</h4>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            List major achievements, awards, and honors
          </label>
          <textarea
          value={editedPlayerData?.achievements || ''}
          onChange={(e) => handleFieldChange('achievements', e?.target?.value)}
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
          placeholder="e.g., League Champion 2021, Top Scorer 2022" />
        
        </div>
      </div>

      {/* Medical Information */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Medical Information</h4>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Medical conditions, injuries, or allergies
          </label>
          <textarea
          value={editedPlayerData?.medicalConditions || ''}
          onChange={(e) => handleFieldChange('medicalConditions', e?.target?.value)}
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
          placeholder="List any medical conditions, past injuries, or allergies" />
        
        </div>
      </div>

      {/* Additional Notes */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Additional Notes</h4>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Any additional information
          </label>
          <textarea
          value={editedPlayerData?.additionalNotes || ''}
          onChange={(e) => handleFieldChange('additionalNotes', e?.target?.value)}
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
          placeholder="Add any other relevant information" />
        
        </div>
      </div>
    </div>;


  const renderOverview = () =>
  <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Personal Information</h4>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Full Name</span>
              <span className="font-medium text-foreground">{playerData?.fullName || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Known As</span>
              <span className="font-medium text-foreground">{playerData?.knownAs || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Date of Birth</span>
              <span className="font-medium text-foreground">{formatDate(playerData?.dateOfBirth)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Age</span>
              <span className="font-medium text-foreground">{calculateAge(playerData?.dateOfBirth)} years</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Place of Birth</span>
              <span className="font-medium text-foreground">{playerData?.placeOfBirth || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Nationality</span>
              <span className="font-medium text-foreground">{playerData?.nationalityPrimary || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Second Nationality</span>
              <span className="font-medium text-foreground">{playerData?.nationalitySecondary || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Height</span>
              <span className="font-medium text-foreground">{playerData?.height ? `${playerData?.height} cm` : 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Weight</span>
              <span className="font-medium text-foreground">{playerData?.weight ? `${playerData?.weight} kg` : 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Preferred Foot</span>
              <span className="font-medium text-foreground">{playerData?.preferredFoot || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Professional Details</h4>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Current Club</span>
              <span className="font-medium text-foreground">{playerData?.currentClub || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">League</span>
              <span className="font-medium text-foreground">{playerData?.currentLeague || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Country</span>
              <span className="font-medium text-foreground">{playerData?.currentCountry || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Position</span>
              <span className="font-medium text-foreground">{formatPosition(playerData?.primaryPosition)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Squad Number</span>
              <span className="font-medium text-foreground">{playerData?.squadNumber || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Contract Type</span>
              <span className="font-medium text-foreground">{playerData?.contractType || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Contract Start</span>
              <span className="font-medium text-foreground">{formatDate(playerData?.contractStartDate)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Contract End</span>
              <span className="font-medium text-foreground">{formatDate(playerData?.contractEndDate)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Market Value</span>
              <span className="font-medium text-foreground">{playerData?.currentMarketValue ? `€${playerData?.currentMarketValue}M` : 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Highest Value</span>
              <span className="font-medium text-foreground">{playerData?.highestMarketValue ? `€${playerData?.highestMarketValue}M` : 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {(playerData?.agentName || playerData?.agencyName) &&
    <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Agent & Representation</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Agent Name</span>
              <span className="font-medium text-foreground">{playerData?.agentName || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Agency</span>
              <span className="font-medium text-foreground">{playerData?.agencyName || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Agent Email</span>
              <span className="font-medium text-foreground">{playerData?.agentEmail || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Agent Phone</span>
              <span className="font-medium text-foreground">{playerData?.agentPhone || 'N/A'}</span>
            </div>
          </div>
        </div>
    }

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Status & Availability</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex justify-between py-2">
            <span className="text-muted-foreground">Available for Transfer</span>
            <span className="font-medium text-foreground">{playerData?.availableForTransfer ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-muted-foreground">Available for Loan</span>
            <span className="font-medium text-foreground">{playerData?.availableForLoan ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-muted-foreground">Verified Status</span>
            <span className="font-medium text-foreground">{playerData?.verifiedStatus ? 'Verified' : 'Unverified'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-muted-foreground">FIFA Verified</span>
            <span className="font-medium text-foreground">{playerData?.fifaVerified ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>
    </div>;


  const renderCareerHistory = () =>
  <div className="space-y-4">
      {loading ?
    <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div> :
    careerHistory?.length > 0 ?
    careerHistory?.map((career, index) =>
    <div key={index} className="flex gap-4 pb-4 border-b border-border last:border-0">
            <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="Building" size={20} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h5 className="font-semibold text-foreground">{career?.clubName}</h5>
                  <p className="text-sm text-muted-foreground">{career?.league} • {career?.country}</p>
                  <p className="text-sm text-muted-foreground">{career?.season}</p>
                </div>
                <span className="text-xs bg-muted px-2 py-1 rounded-md text-muted-foreground">
                  #{career?.squadNumber || '-'}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-muted-foreground">Contract: {career?.contractType}</span>
                {career?.contractStartDate &&
          <span className="text-muted-foreground">{formatDate(career?.contractStartDate)} - {formatDate(career?.contractEndDate)}</span>
          }
              </div>
            </div>
          </div>
    ) :

    <div className="text-center py-8 text-muted-foreground">No career history available</div>
    }
    </div>;


  const renderPerformanceStats = () =>
  <div className="space-y-4">
      {loading ?
    <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div> :
    performanceStats?.length > 0 ?
    performanceStats?.map((stat, index) =>
    <div key={index} className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="font-semibold text-foreground">{stat?.season} - {stat?.competitionType}</h5>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Appearances</div>
                <div className="text-lg font-bold text-foreground">{stat?.appearances || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Goals</div>
                <div className="text-lg font-bold text-primary">{stat?.goalsScored || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Assists</div>
                <div className="text-lg font-bold text-success">{stat?.assists || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Minutes</div>
                <div className="text-lg font-bold text-foreground">{stat?.minutesPlayed || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Started</div>
                <div className="text-sm font-medium text-foreground capitalize">{stat?.gamesStarted || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Substitute</div>
                <div className="text-sm font-medium text-foreground capitalize">{stat?.gamesSubstitute || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Yellow Cards</div>
                <div className="text-sm font-medium text-warning capitalize">{stat?.yellowCards || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Red Cards</div>
                <div className="text-sm font-medium text-destructive capitalize">{stat?.redCards || 0}</div>
              </div>
            </div>
          </div>
    ) :

    <div className="text-center py-8 text-muted-foreground">No performance statistics available</div>
    }
    </div>;


  const renderClubInterest = () =>
  <div className="space-y-4">
      {loading ?
    <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div> :
    clubInterests?.length > 0 ?
    clubInterests?.map((interest, index) =>
    <div key={index} className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h5 className="font-semibold text-foreground">{interest?.club?.fullName || interest?.club?.organization || 'Unknown Club'}</h5>
                <p className="text-sm text-muted-foreground">{interest?.club?.email}</p>
              </div>
              <span className={`px-2 py-1 rounded-md text-xs font-medium ${
        interest?.interestLevel === 'high' ? 'bg-destructive/10 text-destructive' :
        interest?.interestLevel === 'monitoring' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`
        }>
                {interest?.interestLevel}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Priority:</span>
                <span className="ml-2 font-medium text-foreground capitalize">{interest?.priority}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <span className="ml-2 font-medium text-foreground">{interest?.isActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
            {interest?.notes &&
      <div className="text-sm">
                <span className="text-muted-foreground">Notes:</span>
                <p className="mt-1 text-foreground">{interest?.notes}</p>
              </div>
      }
            <div className="text-xs text-muted-foreground">
              Created: {formatDate(interest?.createdAt)}
            </div>
          </div>
    ) :

    <div className="text-center py-8 text-muted-foreground">No club interest recorded</div>
    }
    </div>;


  const renderTransferHistory = () =>
  <div className="space-y-4">
      {loading ?
    <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div> :
    transferHistory?.length > 0 ?
    transferHistory?.map((transfer, index) =>
    <div key={index} className="flex gap-4 pb-4 border-b border-border last:border-0">
            <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="ArrowRightLeft" size={20} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h5 className="font-semibold text-foreground">{transfer?.fromClub} → {transfer?.toClub}</h5>
                  <p className="text-sm text-muted-foreground">{formatDate(transfer?.transferDate)}</p>
                </div>
                <span className="text-xs bg-muted px-2 py-1 rounded-md text-muted-foreground capitalize">
                  {transfer?.transferType}
                </span>
              </div>
              {transfer?.transferFee &&
        <div className="text-sm font-medium text-primary">
                  €{transfer?.transferFee}M
                </div>
        }
            </div>
          </div>
    ) :

    <div className="text-center py-8 text-muted-foreground">No transfer history available</div>
    }
    </div>;


  const renderMarketValue = () =>
  <div className="space-y-4">
      {/* Transfer & Verification Toggles */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-4">
          <h5 className="font-semibold text-foreground text-sm uppercase tracking-wide">Transfer & Verification Status</h5>

          {/* Available for Transfer */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Available for Transfer</p>
              <p className="text-xs text-muted-foreground">Toggle to update transfer availability</p>
            </div>
            <button
          type="button"
          onClick={() => handleToggleSave('availableForTransfer', !playerData?.availableForTransfer)}
          disabled={savingToggles?.availableForTransfer}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          playerData?.availableForTransfer ? 'bg-green-500' : 'bg-gray-300'} ${
          savingToggles?.availableForTransfer ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          aria-label="Toggle available for transfer">
          
              {savingToggles?.availableForTransfer ?
          <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </span> :

          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            playerData?.availableForTransfer ? 'translate-x-6' : 'translate-x-1'}`
            } />

          }
            </button>
          </div>

          {/* Available for Loan */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Available for Loan</p>
              <p className="text-xs text-muted-foreground">Toggle to update loan availability</p>
            </div>
            <button
          type="button"
          onClick={() => handleToggleSave('availableForLoan', !playerData?.availableForLoan)}
          disabled={savingToggles?.availableForLoan}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          playerData?.availableForLoan ? 'bg-green-500' : 'bg-gray-300'} ${
          savingToggles?.availableForLoan ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          aria-label="Toggle available for loan">
          
              {savingToggles?.availableForLoan ?
          <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </span> :

          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            playerData?.availableForLoan ? 'translate-x-6' : 'translate-x-1'}`
            } />

          }
            </button>
          </div>

          {/* FIFA Verified */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1c3e28296-1772629056793.png"
            alt="FIFA logo"
            className="h-5 w-auto object-contain"
            onError={(e) => {e.target.style.display = 'none';}} />
          
              <div>
                <p className="text-sm font-medium text-foreground">FIFA Verified</p>
                <p className="text-xs text-muted-foreground">Toggle to update FIFA verification status</p>
              </div>
            </div>
            <button
          type="button"
          onClick={() => handleToggleSave('fifaVerified', !playerData?.fifaVerified)}
          disabled={savingToggles?.fifaVerified}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          playerData?.fifaVerified ? 'bg-blue-500' : 'bg-gray-300'} ${
          savingToggles?.fifaVerified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          aria-label="Toggle FIFA verified">
          
              {savingToggles?.fifaVerified ?
          <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </span> :

          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            playerData?.fifaVerified ? 'translate-x-6' : 'translate-x-1'}`
            } />

          }
            </button>
          </div>

          {/* CAF Verified */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1076eaf44-1772629058013.png"
            alt="CAF logo"
            className="h-5 w-auto object-contain"
            onError={(e) => {e.target.style.display = 'none';}} />
          
              <div>
                <p className="text-sm font-medium text-foreground">CAF Verified</p>
                <p className="text-xs text-muted-foreground">Toggle to update CAF verification status</p>
              </div>
            </div>
            <button
          type="button"
          onClick={() => handleToggleSave('cafVerified', !playerData?.cafVerified)}
          disabled={savingToggles?.cafVerified}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          playerData?.cafVerified ? 'bg-orange-500' : 'bg-gray-300'} ${
          savingToggles?.cafVerified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          aria-label="Toggle CAF verified">
          
              {savingToggles?.cafVerified ?
          <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </span> :

          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            playerData?.cafVerified ? 'translate-x-6' : 'translate-x-1'}`
            } />

          }
            </button>
          </div>
        </div>

      {loading ?
    <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div> :
    marketValueHistory?.length > 0 ?
    <>
          <div className="bg-primary/10 rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">Current Market Value</div>
            <div className="text-3xl font-bold text-primary">€{playerData?.currentMarketValue || '0.0'}M</div>
            <div className="text-sm text-muted-foreground mt-2">Highest: €{playerData?.highestMarketValue || '0.0'}M</div>
          </div>
          <div className="space-y-3">
            <h5 className="font-semibold text-foreground">Value History</h5>
            {marketValueHistory?.map((value, index) =>
        <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm text-muted-foreground">{formatDate(value?.valuationDate)}</span>
                <span className="text-sm font-medium text-foreground">€{value?.marketValue}M</span>
              </div>
        )}
          </div>
        </> :

    <div className="text-center py-8 text-muted-foreground">No market value history available</div>
    }
    </div>;


  const renderContent = () => {
    if (isFullEditMode) {
      return renderFullEditForm();
    }

    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'career':
        return renderCareerHistory();
      case 'statistics':
        return renderPerformanceStats();
      case 'club-interest':
        return renderClubInterest();
      case 'transfers':
        return renderTransferHistory();
      case 'market':
        return renderMarketValue();
      case 'advanced':case 'competitions':case 'national':case 'medical':case 'media':case 'agent':
        return <div className="text-center py-8 text-muted-foreground">Data for this section is available but not yet displayed in this view</div>;
      default:
        return null;
    }
  };

  const renderEditField = (label, field, type = 'text', options = null) => {
    const value = editedPlayerData?.[field] || '';
    const error = validationErrors?.[field];

    if (type === 'select' && options) {
      return (
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <Select
            value={value}
            onChange={(e) => handleFieldChange(field, e?.target?.value)}
            className={error ? 'border-red-500' : ''}>
            
            {options?.map((opt) =>
            <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            )}
          </Select>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>);

    }

    return (
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <Input
          type={type}
          value={value}
          onChange={(e) => handleFieldChange(field, e?.target?.value)}
          className={error ? 'border-red-500' : ''} />
        
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>);

  };

  if (!isOpen) return null;

  return (
    <>
      {isOpen &&
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <Image
                  src={photoPreview || playerData?.profilePhotoUrl || 'https://images.unsplash.com/photo-1565547213480-b1eaf931f3cd'}
                  alt={`${playerData?.fullName} profile photo`}
                  className="w-full h-full object-cover" />
                
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground font-headline">{playerData?.fullName}</h2>
                  <p className="text-muted-foreground">{formatPosition(playerData?.primaryPosition)} • {calculateAge(playerData?.dateOfBirth)} years • {playerData?.nationalityPrimary}</p>
                  {isAdmin &&
                <div className="flex items-center gap-2 mt-1">
                      <Icon name="ShieldCheck" size={14} className="text-primary" />
                      <span className="text-xs text-primary font-medium">
                        {isFullEditMode ? 'Full Edit Mode - All fields editable' : 'Admin View - Click "Open Full Editor" to edit all fields'}
                      </span>
                    </div>
                }
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
            </div>

            {/* Tabs - Hide when in full edit mode */}
            {!isFullEditMode &&
          <div className="border-b border-border overflow-x-auto">
                <div className="flex gap-1 p-2 min-w-max">
                  {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground' :
                'text-muted-foreground hover:bg-muted hover:text-foreground'}`
                }>
                
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                      {tab?.count !== undefined && tab?.count > 0 &&
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-background/20 text-xs">
                          {tab?.count}
                        </span>
                }
                    </button>
              )}
                </div>
              </div>
          }

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {renderContent()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Last updated: {formatDate(playerData?.updatedAt)}
              </div>
              <div className="flex gap-2">
                {isFullEditMode ?
              <>
                    <Button
                  variant="outline"
                  onClick={toggleFullEditMode}
                  disabled={saving}>
                  
                      Cancel
                    </Button>
                    <Button
                  variant="default"
                  onClick={handleSaveClick}
                  disabled={saving || Object.keys(validationErrors)?.length > 0}
                  loading={saving}
                  className="bg-green-500 hover:bg-green-600 text-white">
                  
                      <Icon name="Save" size={16} className="mr-2" />
                      Save All Changes
                    </Button>
                  </> :

              <>
                    <Button variant="outline" onClick={onClose}>
                      Close
                    </Button>
                    {isAdmin &&
                <Button variant="default" onClick={toggleFullEditMode} iconName="Edit">
                        Open Full Editor
                      </Button>
                }
                  </>
              }
              </div>
            </div>
          </div>
        </div>
      }

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={saveAllChanges}
        title="Confirm Changes"
        message={`Are you sure you want to save all changes to ${playerData?.fullName}'s profile? This will update the player information and notify relevant stakeholders.`}
        confirmText="Save Changes"
        cancelText="Cancel"
        variant="default"
        loading={saving} />
      
    </>);

};

export default AdminPlayerProfileModal;